export type DirectionBlock =
  | { kind: 'cycleHeading'; groupIndex: number }
  | { kind: 'placeRow'; groupIndex: number; placeIndex: number; rowIndex: number }
  | { kind: 'placeSubtotal'; groupIndex: number; placeIndex: number }
  | { kind: 'groupTotal'; groupIndex: number }
  | { kind: 'deleted'; deletedIndex: number }
  | { kind: 'memo' };

// コース（1 shift）の描画対象を「行レベル」の順序付きブロック列に変換する。
// 地点(place)の明細は 1 行ずつ独立ブロック（placeRow）にし、行単位で改ページ可能にする。
// 並び順: 各 group で showCycleHeading?→cycleHeading、各 place で placeRow×行数 ＋ hasSubtotal?→placeSubtotal、
//         group 末に showGroupTotal?→groupTotal。全 group 後に deleted×deletedCount、最後に hasMemo?→memo。
export function buildDirectionBlocks(params: {
  // [groupIndex][placeIndex] = mergeBySameRow 後の表示行数。
  placeRowCounts: number[][];
  // [groupIndex][placeIndex] = その地点に小計を出すか（renderPlaceBlock の小計表示条件）。
  hasSubtotal: boolean[][];
  showCycleHeading: boolean;
  showGroupTotal: boolean;
  deletedCount: number;
  // 備考欄を末尾ブロックとして 1 つ加えるか。
  hasMemo: boolean;
}): DirectionBlock[] {
  const {
    placeRowCounts, hasSubtotal, showCycleHeading, showGroupTotal, deletedCount, hasMemo,
  } = params;
  const blocks: DirectionBlock[] = [];
  placeRowCounts.forEach((placesInGroup, groupIndex) => {
    if (showCycleHeading) blocks.push({ kind: 'cycleHeading', groupIndex });
    placesInGroup.forEach((rowCount, placeIndex) => {
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        blocks.push({
          kind: 'placeRow', groupIndex, placeIndex, rowIndex,
        });
      }
      if (hasSubtotal[groupIndex]?.[placeIndex]) {
        blocks.push({ kind: 'placeSubtotal', groupIndex, placeIndex });
      }
    });
    if (showGroupTotal) blocks.push({ kind: 'groupTotal', groupIndex });
  });
  for (let deletedIndex = 0; deletedIndex < deletedCount; deletedIndex += 1) {
    blocks.push({ kind: 'deleted', deletedIndex });
  }
  if (hasMemo) blocks.push({ kind: 'memo' });
  return blocks;
}

export type PageMeta = { pageNumber: number; totalPages: number; blockIndices: number[] };

export function withPageNumbers(pages: number[][]): PageMeta[] {
  return pages.map((blockIndices, i) => ({ pageNumber: i + 1, totalPages: pages.length, blockIndices }));
}

// heights[i] = i番目ブロックの実測高さ。ブロックは不可分。available超えの単独ブロックは単独ページ。
// keepWithNext[i]===true のブロックは次ブロック i+1 と必ず同じページに置く（見出しがページ末尾に
// 孤立して次ページが見出し無しで始まるのを防ぐ）。i を置くと i がページ末尾になり、かつ i+1 が
// 同ページに入らない場合は、i の前で改ページして i から新ページに置く。i が最後の要素なら通常どおり。
export function paginateByHeights(
  heights: number[],
  available: number,
  keepWithNext?: boolean[],
): number[][] {
  const pages: number[][] = [];
  let current: number[] = [];
  let used = 0;
  heights.forEach((h, i) => {
    // 通常の改ページ判定: current に何か載っていて i を載せると溢れるなら改ページ。
    if (current.length > 0 && used + h > available) {
      pages.push(current);
      current = [];
      used = 0;
    }
    // 見出し系: i を載せると i がページ末尾になり次ブロックが同ページに入らないなら、
    // 先に改ページして i を新ページ先頭へ送る（i 単独で改ページしないよう current が空でない時のみ）。
    if (
      keepWithNext?.[i]
      && i + 1 < heights.length
      && current.length > 0
      && used + h + heights[i + 1] > available
    ) {
      pages.push(current);
      current = [];
      used = 0;
    }
    current.push(i);
    used += h;
  });
  if (current.length > 0) pages.push(current);
  return pages;
}
